import { LABEL_RISE, leaderPath, LEADER_MIN, monoWidth, type Frame } from './frame.ts';
import styles from './charts.module.css';

/* ==========================================================================
   ONE CALLOUT IN THE RIGHT GUTTER.

   Both kinds of number the gutter carries — the reading the lane is quoting
   and each limit the channel publishes — are drawn by this, so they cannot
   drift apart. A callout is three marks:

     · a 4px stub at the plot's right edge, at the height the number is
       about: the trace's own point for a reading, the rule for a limit.
     · the number, right-aligned in the value column, with its unit in the
       unit column beside it, so `79.9 %`, `0.46 m/s` and `81.5 GiB` align
       on their digits down the whole stack (§4).
     · where the number had to move to stay legible, a hairline leading from
       the stub to it — so a displaced number is still unmistakably its own
       height's.

   The stub is what makes the column readable. Without one, a number in this
   gutter is just a number in a list, and an operator has no way to tell the
   battery's 7.9 % from the 15 % rule it has fallen through.
   ========================================================================== */

export function GutterCallout({
  frame,
  anchorY,
  labelY,
  stroke,
  strokeOpacity = 1,
  variant,
  value,
  unit,
  valueProps,
  unitProps,
}: {
  frame: Frame;
  /** The height the number is about, or null when there is no such height. */
  anchorY: number | null;
  /** Where the number goes, or null when the lane had no room for it. */
  labelY: number | null;
  stroke: string;
  strokeOpacity?: number;
  /** `reading` is the lane's own quote; `limit` is a published threshold. */
  variant: 'reading' | 'limit';
  value: string;
  /** Omitted for a readout with nothing to quote — `—` carries no unit. */
  unit: string | null;
  valueProps?: Record<string, string | boolean | undefined>;
  unitProps?: Record<string, string | boolean | undefined>;
}) {
  const valueClass = variant === 'reading' ? styles.gutterValue : styles.limitValue;
  const unitClass = variant === 'reading' ? styles.gutterUnit : styles.limitUnit;
  const displaced =
    anchorY !== null && labelY !== null && Math.abs(labelY - anchorY - LABEL_RISE) > LEADER_MIN;
  /* THE READING OUTRANKS THE RULES, IN THE INK AS WELL AS IN THE PLACEMENT.

     A limit is a constant somebody typed into a config file; the reading is
     the robot. Their labels already differ by a step of size and of colour,
     but at the stub — where the eye actually decides which mark belongs to
     the trace — they were one hairline each and identical. The reading's is
     now a 2px tick a pixel longer, so a column of three callouts reads as
     one measurement against two rules even before a number is read. */
  const stubW = variant === 'reading' ? 2 : 1;
  const stubLen = variant === 'reading' ? 5 : 4;
  /* The label's own left edge, so a leader stops where the digits begin. The
     reading is set one step larger than a limit, so the two are measured at
     their own sizes rather than at a shared guess. */
  const labelLeft = frame.valueX - monoWidth(value.length, variant === 'reading' ? 11 : 10);
  return (
    <>
      {anchorY !== null &&
        (displaced ? (
          <>
            {/* The stub keeps its weight even when a leader carries the
                number away: the mark at the trace is the claim, the elbow is
                only how the label got to where it could be read. */}
            <line
              x1={frame.x1} x2={frame.x1 + stubLen} y1={anchorY} y2={anchorY}
              stroke={stroke}
              strokeOpacity={strokeOpacity}
              strokeWidth={stubW}
              shapeRendering="crispEdges"
            />
            <path
              d={leaderPath(frame.x1, anchorY, labelY as number, labelLeft - 3)}
              fill="none"
              stroke={stroke}
              strokeOpacity={strokeOpacity * 0.55}
              strokeWidth={1}
              shapeRendering="crispEdges"
            />
          </>
        ) : (
          <line
            x1={frame.x1} x2={frame.x1 + stubLen} y1={anchorY} y2={anchorY}
            stroke={stroke}
            strokeOpacity={strokeOpacity}
            strokeWidth={stubW}
            shapeRendering="crispEdges"
          />
        ))}
      {labelY !== null && (
        <>
          <text
            className={valueClass}
            x={frame.valueX}
            y={labelY}
            textAnchor="end"
            {...valueProps}
          >
            {value}
          </text>
          {unit !== null && (
            <text className={unitClass} x={frame.unitX} y={labelY} {...unitProps}>
              {unit}
            </text>
          )}
        </>
      )}
    </>
  );
}
